import { Box, Skeleton, SxProps } from '@mui/material';
import { useEffect, useRef, useState, VideoHTMLAttributes } from 'react';
import { FullScreen } from 'react-full-screen';
import { RenderVideoTimeMarker } from '../types';
import { VideoControls, VideoControlsProps } from '../VideoControls';
import { useVideoControls } from './useVideoControls';

export type UseVideoPlayerProps = {
  videoUrl?: string;
  hideControls?: boolean;
  componentsProps?: {
    videoBox?: VideoHTMLAttributes<HTMLVideoElement> & {
      sx?: SxProps;
    };
    videoControlsBox?: Partial<VideoControlsProps>;
  };
};

export const useVideoPlayer = ({
  videoUrl,
  componentsProps,
  hideControls = false,
}: UseVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoHeight, setVideoHeight] = useState(0);
  const {
    onSeekTime,
    handleFullScreen,
    onFullScreen,
    onSeekBackward,
    onSeekForward,
    onSeekbarClicked,
    setDuration,
    isFullScreen,
    setIsFullScreen,
    duration,
    isMuted,
    onToggleMute,
  } = useVideoControls(videoRef);

  // current time of video
  const [currentTime, setCurrentTime] = useState(0);
  // update seekbar value according to current time
  const [seekbarValue, setSeekbarValue] = useState(0);
  // set default normal playback speed
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  const [renderVideoTimeMarkers, setRenderVideoTimeMarkers] = useState<
    RenderVideoTimeMarker[]
  >([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!videoRef || !videoRef.current) return;

    const video = videoRef.current;
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setSeekbarValue((video.currentTime / video.duration) * 100);
    };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
    // eslint-disable-next-line
  }, [videoRef.current, currentTime, duration]);

  useEffect(() => {
    const handleResize = () => {
      if (videoRef.current) {
        setVideoHeight(videoRef.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const playVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const videoUI = (
    <Box position="relative">
      <FullScreen
        handle={handleFullScreen}
        onChange={(isFullScreen) => {
          if (!isFullScreen) {
            setIsFullScreen(false);
          }
        }}
        className="fullscreen"
      >
        <Box
          sx={{
            visibility: loading ? 'visible' : 'hidden',
            position: 'absolute',
            inset: 0,
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 4,
            }}
          />
        </Box>
        <Box
          ref={videoRef}
          sx={{
            visibility: loading ? 'hidden' : 'visible',
            width: '100%',
            height: '100%',
            borderRadius: 4,
            ...componentsProps?.videoBox?.sx,
          }}
          onLoadedMetadata={() => {
            if (videoRef.current) {
              setDuration(videoRef.current.duration);
            }
          }}
          {...componentsProps?.videoBox}
          onLoadedData={(e) => {
            setLoading(false);
            if (videoRef.current) {
              setVideoHeight(videoRef.current.offsetHeight);
            }
            componentsProps?.videoBox?.onLoadedData?.(e);
          }}
          onLoadStart={() => {
            setLoading(true);
          }}
          component="video"
          src={videoUrl}
          onError={(e) => {
            console.error(e);
            componentsProps?.videoBox?.onError?.(e);
          }}
          muted={isMuted}
          onClick={() => {
            if (videoRef.current) {
              if (videoRef.current.paused) {
                videoRef.current.play();
              } else {
                videoRef.current.pause();
              }
            }
          }}
        />
        {!hideControls && (
          <VideoControls
            videoRef={videoRef}
            duration={duration}
            currentTime={currentTime}
            seekbarValue={seekbarValue}
            onSeekbarClicked={onSeekbarClicked}
            onSeekBackward={onSeekBackward}
            onSeekForward={onSeekForward}
            playVideo={playVideo}
            playbackSpeed={playbackSpeed}
            setPlaybackSpeed={setPlaybackSpeed}
            isFullScreen={isFullScreen}
            onFullScreen={onFullScreen}
            renderVideoTimeMarkers={renderVideoTimeMarkers}
            isMuted={isMuted}
            onToggleMute={onToggleMute}
            {...componentsProps?.videoControlsBox}
          />
        )}
      </FullScreen>
    </Box>
  );

  return {
    videoUI,
    videoHeight,
    currentTime,
    controls: {
      seekTime: onSeekTime,
      onToggleMute,
      isMuted,
    },
    UI: { setRenderVideoTimeMarkers },
    videoRef,
  };
};
